import { DateField, ReferenceField, ReferenceManyField, TextField } from '@/components/admin';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Download, FileUp, History } from 'lucide-react';
import { useCreate, useNotify, useRecordContext, useRefresh } from 'ra-core';
import { useState } from 'react';

export const DesignVersionHistory = () => {
    const record = useRecordContext();
    const [uploadOpen, setUploadOpen] = useState(false);
    
    // For a real app this would upload to Supabase Storage, but here we just mock the URL/input
    const [fileUrl, setFileUrl] = useState('');
    const [notes, setNotes] = useState('');
    
    const [create, { isPending }] = useCreate();
    const notify = useNotify();
    const refresh = useRefresh();

    if (!record) return null;

    const handleUploadVersion = async () => {
        if (!fileUrl) {
            notify('Please provide a file URL', { type: 'warning' });
            return;
        }

        try {
            const nextVersion = (record.version || 0) + 1;
            
            await create('design_versions', {
                data: {
                    design_request_id: record.id,
                    version_number: nextVersion,
                    file_url: fileUrl,
                    notes: notes,
                }
            });

            notify(`Version ${nextVersion} uploaded!`, { type: 'success' });
            setUploadOpen(false);
            setFileUrl('');
            setNotes('');
            refresh();
        } catch (error) {
            notify('Error uploading version', { type: 'error' });
        }
    };

    return (
        <Card>
            <CardHeader className="pb-3 border-b bg-slate-50/50">
                <div className="flex justify-between items-center">
                    <CardTitle className="text-sm font-bold uppercase tracking-wider text-slate-500 flex items-center gap-2">
                        <History className="w-4 h-4" /> Version History
                    </CardTitle>
                    <Button size="sm" variant="outline" className="h-8" onClick={() => setUploadOpen(true)}>
                        <FileUp className="w-3.5 h-3.5 mr-2" /> Upload New Version
                    </Button>
                </div>
            </CardHeader>
            <CardContent className="pt-6">
                <ReferenceManyField reference="design_versions" target="design_request_id" sort={{ field: 'version_number', order: 'DESC' }}>
                    <div className="space-y-4">
                        <VersionList />
                    </div>
                </ReferenceManyField>
            </CardContent>

            <Dialog open={uploadOpen} onOpenChange={setUploadOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Upload New Design Version</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                        <div className="space-y-2">
                            <label className="text-sm font-medium">File URL</label>
                            <Input 
                                placeholder="https://..." 
                                value={fileUrl}
                                onChange={(e) => setFileUrl(e.target.value)}
                            />
                        </div>
                        <div className="space-y-2">
                            <label className="text-sm font-medium">Notes / Changelog</label>
                            <Input 
                                placeholder="What changed in this version?" 
                                value={notes}
                                onChange={(e) => setNotes(e.target.value)}
                            />
                        </div>
                        <Button 
                            className="w-full" 
                            onClick={handleUploadVersion} 
                            disabled={isPending || !fileUrl}
                        >
                            Upload Version {record.version ? record.version + 1 : 1}
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>
        </Card>
    );
};

// We need a subcomponent to access the list context from ReferenceManyField
import { useListContext } from 'ra-core';

const VersionList = () => {
    const { data, isLoading } = useListContext();
    
    if (isLoading) return <div className="text-sm text-slate-500">Loading...</div>;
    if (!data || data.length === 0) return <div className="text-sm text-slate-500">No versions uploaded yet.</div>;

    return (
        <div className="space-y-3">
            {data.map((version: any) => (
                <div key={version.id} className="flex justify-between items-center py-3 border-b border-slate-100 last:border-0">
                    <div className="flex gap-4">
                        <div className="w-10 h-10 rounded-lg bg-blue-50 border border-blue-100 flex items-center justify-center font-bold text-blue-700">
                            v{version.version_number}
                        </div>
                        <div>
                            <div className="text-sm font-semibold">{version.notes || `Version ${version.version_number}`}</div>
                            <div className="text-xs text-slate-500 flex gap-2 mt-1">
                                <span><DateField record={version} source="created_at" showTime /></span>
                                <span>•</span>
                                <span>by <ReferenceField record={version} source="created_by" reference="sales" link={false}><TextField source="first_name" /></ReferenceField></span>
                            </div>
                        </div>
                    </div>
                    {version.file_url && (
                        <Button size="sm" variant="ghost" className="text-blue-600 hover:text-blue-800" asChild>
                            <a href={version.file_url} target="_blank" rel="noreferrer">
                                <Download className="w-4 h-4" />
                            </a>
                        </Button>
                    )}
                </div>
            ))}
        </div>
    );
};
